import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ModeService } from '../../../core/services/mode.service';
import { AuthService } from '../../../core/services/auth.service';
import { ONBOARDING_ENTRY_PATH } from '../../../core/onboarding/onboarding-entry';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-admin-learner-toggle',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './admin-learner-toggle.component.html',
  styleUrls: ['./admin-learner-toggle.component.scss'],
})
export class AdminLearnerToggleComponent {
  mode = inject(ModeService);
  private router = inject(Router);
  private auth = inject(AuthService);

  /** Flag to prevent effect from redirecting during initial navigation */
  private initialNavigationComplete = false;

  constructor() {
    // Wait for first NavigationEnd before allowing effect redirects
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => {
        this.initialNavigationComplete = true;
      });

    effect(() => {
      const m = this.mode.mode(); // 'admin' | 'learner'

      // Don't redirect during initial navigation - let the router handle it
      if (!this.initialNavigationComplete) return;

      const url = this.router.url.split('?')[0]; // Remove query params
      const isRoot = url === '/' || url === '';

      if (isRoot) {
        queueMicrotask(() => {
          this.router.navigate([m === 'learner' ? '/dashboard' : '/user-management'], { replaceUrl: true });
        });
      }
    });
  }

  /**
   * Pages that are shared between admin and learner modes.
   * No redirect needed when switching modes on these pages.
   */
  private isNeutralPage(url: string): boolean {
    const path = url.split('?')[0]; // Remove query params
    return (
      path === '/' ||
      path === '' ||
      path.startsWith('/profile') ||
      path.startsWith('/course-catalog') ||
      path.startsWith('/course-details') ||
      path.startsWith('/learning-path-details') ||
      path.startsWith('/checkout') ||
      path.startsWith('/welcome')
    );
  }

  /**
   * Admin-specific pages (user will be redirected to dashboard when switching to learner)
   */
  private isAdminPage(url: string): boolean {
    const path = url.split('?')[0];
    return (
      path.startsWith('/user-management') ||
      path.startsWith('/create-user') ||
      path.startsWith('/product-management') ||
      path.startsWith('/category-management') ||
      path.startsWith('/memberships') ||
      path.startsWith('/admin/') ||
      path.startsWith('/groups') ||
      path.startsWith('/reports')
    );
  }

  /**
   * Learner-specific pages (user will be redirected to user-management when switching to admin)
   */
  private isLearnerPage(url: string): boolean {
    const path = url.split('?')[0];
    return (
      path.startsWith('/dashboard') ||
      path.startsWith('/my-learning') ||
      path.startsWith('/settings') ||
      path.startsWith('/my-achievements')
    );
  }

  switch(m: 'admin' | 'learner') {
    this.mode.set(m);

    const currentUrl = this.router.url;

    // First-time Learner View for a Group Admin → run them through the Learner First UX.
    // Optimistically clear the client-side flag so a rapid toggle doesn't re-trigger the
    // redirect before the next /me response updates the signal.
    if (m === 'learner' && this.auth.shouldTriggerLearnerFirstUX()) {
      this.auth.shouldTriggerLearnerFirstUX.set(false);
      queueMicrotask(() => {
        this.router.navigate([ONBOARDING_ENTRY_PATH], { replaceUrl: true });
      });
      return;
    }

    // Stay on neutral pages (profile, course-catalog, etc.)
    if (this.isNeutralPage(currentUrl)) {
      return;
    }

    // Switching to Learner mode
    if (m === 'learner') {
      // If on admin page, redirect to dashboard
      if (this.isAdminPage(currentUrl)) {
        queueMicrotask(() => {
          this.router.navigate(['/dashboard'], { replaceUrl: true });
        });
      }
      return;
    }

    // Switching to Admin mode
    if (m === 'admin') {
      // If on learner page, redirect to user-management
      if (this.isLearnerPage(currentUrl)) {
        queueMicrotask(() => {
          this.router.navigate(['/user-management'], { replaceUrl: true });
        });
      }
      return;
    }
  }
}
